import { SortBy } from '@/constant/common'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC } from 'react'
import { useLocation } from 'react-router-dom'
import classes from './styles.module.scss'
import { CisFertigation } from '@/types/cisFertigation'

interface FertigationTableProps {
  rows: CisFertigation[]
  isParallelMode: boolean
  onEditItem: (id: string) => void
  onDeleteItem: (id: string) => void
  handleSort: (sortBy: SortBy, sortDirection: 'desc' | 'asc') => void
}

const FertigationTable: FC<FertigationTableProps> = ({
  rows,
  isParallelMode,
  onEditItem,
  onDeleteItem,
}) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermission()

  const handleEditItem = (id: string) => {
    // ViewOnly users cannot edit fertigation
    if (isViewOnly) return

    onEditItem(id)
  }
  const handleDeleteItem = (id: string) => {
    // ViewOnly users cannot delete fertigation
    if (isViewOnly) return

    onDeleteItem(id)
  }

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table
          sx={{
            minWidth: 650,
            border: '1px solid #E0E0E0',
            borderCollapse: 'collapse',
            '& th, & td': {
              border: '1px solid #E0E0E0',
            },
          }}
          aria-label="simple table"
        >
          <TableHead
            sx={{
              backgroundColor: '#EFEFEF',
              '& th': {
                textAlign: 'center',
                verticalAlign: 'middle',
                fontWeight: 600,
              },
            }}
          >
            <TableRow>
              <TableCell colSpan={1}></TableCell>
              {isParallelMode && (
                <TableCell colSpan={2}>{t('Fertigation Line')}</TableCell>
              )}
              <TableCell colSpan={2}>{t('Fertigation Control')}</TableCell>
              <TableCell colSpan={2}>
                {t('Fertigation Tank Management')}
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '150px',
                  width: '100px',
                }}
              ></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('fertiliserName')}</TableCell>
              {isParallelMode && <TableCell>{t('Inlet Valve')}</TableCell>}
              {isParallelMode && <TableCell>{t('Fertigation Pump')}</TableCell>}
              <TableCell>{t('Injection Valve')}</TableCell>
              <TableCell>{t('flowSensor')}</TableCell>
              <TableCell>{t('agitatorValve')}</TableCell>
              <TableCell>{t('waterLevelSensor')}</TableCell>
              <TableCell
                sx={{
                  minWidth: '150px',
                  width: '100px',
                }}
              >
                {t('commonTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: 'common.white',
              '& td': {
                textAlign: 'center',
                verticalAlign: 'middle',
              },
            }}
          >
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                {isParallelMode && (
                  <TableCell component="th" scope="row">
                    {row.fertiliser?.name ?? '-'}
                  </TableCell>
                )}
                {isParallelMode && (
                  <TableCell component="th" scope="row">
                    {row.pumpStart?.name ?? '-'}
                  </TableCell>
                )}
                <TableCell component="th" scope="row">
                  {row.flushing?.name ?? '-'}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.flowSensor?.name ?? '-'}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.agitator?.name ?? '-'}
                </TableCell>
                <TableCell component="th" scope="row">
                  {row.waterLevel?.name ?? '-'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing="10px">
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        color: 'primary.main',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        opacity: isViewOnly ? 0.6 : 1,
                      }}
                      onClick={() => handleEditItem(`${row.id}`)}
                    >
                      {t('actionEdit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        color: 'grey.600',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        opacity: isViewOnly ? 0.6 : 1,
                      }}
                      onClick={() => handleDeleteItem(`${row.id}`)}
                    >
                      {t('actionDelete')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FertigationTable
